"use client";

import { createContext, useContext, useState, useRef, useCallback, useEffect, useMemo } from "react";
import { usePathname } from "next/navigation";
import useSWR from "swr";
import type { StepProgress } from "@/lib/refresh/pipeline";
import type { RefreshStatusResponse } from "@/lib/refresh/status";
import { useStore } from "./StoreContext";

export type { StepProgress };

export type RefreshStatus = "idle" | "running" | "done" | "warn" | "error" | "auth_error";

interface RefreshState {
  jobId: number | null;
  status: RefreshStatus;
  steps: StepProgress[];
  durationMs: number | null;
  startedAt: number | null;
  lastJobAt: number | null; // completedAt of last finished job
  targetStoreId: string | null;
  hasErrors: boolean;
  modalOpen: boolean;
}

interface RefreshCtx extends RefreshState {
  /** Запустить обновление. Без аргументов — обычный быстрый refresh; с
   *  includeDailyAnalytics — глубокая сверка; с stepIds — частичный. */
  startRefresh: (
    stepIds?: string[],
    storeId?: string | null,
    options?: { includeDailyAnalytics?: boolean; openModal?: boolean },
  ) => Promise<void>;
  attachToJob: (jobId: number, openModal?: boolean) => void;
  retryStep: (stepId: string) => void;    // placeholder — triggers full refresh for now
  openModal: () => void;
  closeModal: () => void;
  copyLog: () => string;
}

const RefreshContext = createContext<RefreshCtx | null>(null);
const VIEW_REFRESH_STATUS_DELAY_MS = 12_000;
const VIEW_REFRESH_STATUS_FALLBACK_MS = 30_000;
const SCOPE_STATUS_IDLE_REFRESH_MS = 30_000;
const SCOPE_STATUS_RUNNING_REFRESH_MS = 2_000;

async function refreshStatusFetcher(url: string): Promise<RefreshStatusResponse> {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(`refresh status ${res.status}`);
  return res.json();
}

export function useRefresh(): RefreshCtx {
  const ctx = useContext(RefreshContext);
  if (!ctx) throw new Error("useRefresh must be used inside RefreshProvider");
  return ctx;
}

export function useOptionalRefresh(): RefreshCtx | null {
  return useContext(RefreshContext);
}

export function useRefreshScopeStatus(scopeQuery = "", enabled = true) {
  const key = enabled ? `/api/refresh/status${scopeQuery || ""}` : null;
  const swr = useSWR<RefreshStatusResponse>(key, refreshStatusFetcher, {
    refreshInterval: data => data?.aggregate.status === "running"
      ? SCOPE_STATUS_RUNNING_REFRESH_MS
      : SCOPE_STATUS_IDLE_REFRESH_MS,
    revalidateOnFocus: true,
    keepPreviousData: true,
  });
  return swr;
}

export function RefreshProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const publicPage = !!pathname?.startsWith("/shared/");
  const [state, setState] = useState<RefreshState>({
    jobId: null,
    status: "idle",
    steps: [],
    durationMs: null,
    startedAt: null,
    lastJobAt: null,
    targetStoreId: null,
    hasErrors: false,
    modalOpen: false,
  });

  const esRef = useRef<EventSource | null>(null);
  const activeStreamJobIdRef = useRef<number | null>(null);
  const activeStreamStoreIdRef = useRef<string | null>(null);

  // Активный store — когда он меняется, сбрасываем стейт и перечитываем last
  // job магазина B (иначе юзер видел бы прогресс магазина A при переключении).
  const { mode: storeMode, store } = useStore();
  const currentStoreId = store?.id ?? null;
  const deferStatusUntilRouteReady = !!pathname?.startsWith("/view/");

  useEffect(() => {
    // При смене store закрываем SSE (оно от другого store'а), сбрасываем state,
    // и перечитываем /api/refresh для текущего store.
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    activeStreamJobIdRef.current = null;
    activeStreamStoreIdRef.current = null;
    setState({
      jobId: null,
      status: "idle",
      steps: [],
      durationMs: null,
      startedAt: null,
      lastJobAt: null,
      targetStoreId: null,
      hasErrors: false,
      modalOpen: false,
    });
    if (!currentStoreId || publicPage) return;

    const loadCurrentRefresh = () => {
      // Если из новой шапки запустили обновление другого доступного магазина,
      // не даём фоновому опросу активного магазина перетереть его SSE-прогресс.
      if (
        activeStreamJobIdRef.current != null &&
        activeStreamStoreIdRef.current != null &&
        activeStreamStoreIdRef.current !== currentStoreId
      ) return;
      const params = new URLSearchParams({ storeId: currentStoreId, includeSteps: "1" });
      fetch(`/api/refresh/status?${params.toString()}`, { cache: "no-store" })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        const storeStatus = d?.stores?.[0];
        const job = storeStatus?.lastJob;
        if (!job) return;
        if (storeStatus.isRunning) {
          if (activeStreamJobIdRef.current !== job.id || activeStreamStoreIdRef.current !== currentStoreId) {
            connectToStream(job.id, false, currentStoreId);
          }
        } else {
          const steps = Array.isArray(job.steps) ? job.steps : [];
          const hasErrors = steps.some((s: StepProgress) => s.status === "error" || s.status === "warn");
          setState(prev => {
            // Отказ до создания задания — результат последней попытки. Фоновый
            // опрос старого задания не должен скрывать причину этого отказа.
            if (prev.targetStoreId === currentStoreId &&
                (prev.status === "auth_error" || prev.status === "error") &&
                prev.steps.some(step => step.stepId === "init" && step.status === "error")) return prev;
            // Результат явно выбранного соседнего магазина оставляем в открытом
            // окне до закрытия. Иначе очередной poll текущего магазина внезапно
            // подменял этапы сразу после завершения задания.
            if (
              prev.targetStoreId != null &&
              prev.targetStoreId !== currentStoreId &&
              (prev.status === "running" || prev.modalOpen)
            ) return prev;
            // Тот же завершённый джоб — НЕ создаём новый объект состояния, иначе
            // каждый опрос (раз в 8с) ре-рендерит всех потребителей контекста и
            // индикатор Next «постоянно мигает». Возврат prev → React не ре-рендерит.
            if (
              prev.jobId === job.id &&
              prev.status === (job.status as RefreshStatus) &&
              prev.targetStoreId === currentStoreId &&
              prev.hasErrors === hasErrors &&
              prev.durationMs === (job.durationMs ?? null) &&
              prev.lastJobAt === (job.completedAt ?? null)
            ) {
              return prev;
            }
            return {
              ...prev,
              jobId: job.id,
              status: job.status as RefreshStatus,
              steps,
              durationMs: job.durationMs ?? null,
              lastJobAt: job.completedAt ?? null,
              targetStoreId: currentStoreId,
              hasErrors,
            };
          });
        }
      })
      .catch(() => {});
    };
    let started = false;
    let interval: number | null = null;
    let fallback: number | null = null;
    let startDelay: number | null = null;

    const startRefreshStatusPolling = () => {
      if (started) return;
      started = true;
      loadCurrentRefresh();
      interval = window.setInterval(loadCurrentRefresh, 8000);
    };

    if (deferStatusUntilRouteReady) {
      const onRouteReady = () => {
        if (fallback != null) {
          window.clearTimeout(fallback);
          fallback = null;
        }
        startDelay = window.setTimeout(startRefreshStatusPolling, VIEW_REFRESH_STATUS_DELAY_MS);
      };
      window.addEventListener("frogstat:route-ready", onRouteReady, { once: true });
      fallback = window.setTimeout(startRefreshStatusPolling, VIEW_REFRESH_STATUS_FALLBACK_MS);
      return () => {
        window.removeEventListener("frogstat:route-ready", onRouteReady);
        if (fallback != null) window.clearTimeout(fallback);
        if (startDelay != null) window.clearTimeout(startDelay);
        if (interval != null) window.clearInterval(interval);
      };
    }

    startRefreshStatusPolling();
    return () => {
      if (interval != null) window.clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStoreId, publicPage]);

  // Cleanup при unmount — закрыть SSE чтобы не течь.
  useEffect(() => {
    return () => {
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      activeStreamJobIdRef.current = null;
      activeStreamStoreIdRef.current = null;
    };
  }, []);

  const connectToStream = useCallback((jobId: number, openModal: boolean, storeId?: string | null) => {
    esRef.current?.close();
    activeStreamJobIdRef.current = jobId;
    const streamStoreId = storeId ?? currentStoreId;
    activeStreamStoreIdRef.current = streamStoreId ?? null;

    const params = new URLSearchParams({ jobId: String(jobId) });
    if (streamStoreId) params.set("storeId", streamStoreId);
    const es = new EventSource(`/api/refresh/stream?${params.toString()}`);
    esRef.current = es;

    setState(prev => ({
      ...prev,
      jobId,
      status: "running",
      steps: prev.steps.length > 0 ? prev.steps : [],
      startedAt: Date.now(),
      targetStoreId: streamStoreId ?? null,
      modalOpen: openModal ? true : prev.modalOpen,
    }));

    es.onmessage = (e) => {
      // SSE-сообщения парсим в try/catch: если сервер пришлёт битый JSON
      // (оборвалось соединение, баг в /api/refresh), без обёртки исключение
      // убивало бы весь EventSource-обработчик и дальнейшие апдейты переставали
      // приходить — пользователь видел бы «застрявший» прогресс. Теперь просто
      // пропускаем кривое сообщение и ждём следующее.
      let event;
      try {
        event = JSON.parse(e.data);
      } catch (err) {
        console.warn("[refresh] failed to parse SSE event:", err);
        return;
      }

      if (event.type === "snapshot" || event.type === "progress") {
        const steps: StepProgress[] = event.steps ?? [];
        setState(prev => ({
          ...prev,
          steps,
          status: "running",
        }));
      }

      if (event.type === "complete") {
        const steps: StepProgress[] = event.steps ?? [];
        const hasErrors = steps.some(s => s.status === "error" || s.status === "warn");
        const finalStatus: RefreshStatus =
            event.status === "auth_error" ? "auth_error"
          : event.status === "error"      ? "error"
          : event.status === "warn"       ? "warn"
          : "done";

        setState(prev => ({
          ...prev,
          steps,
          status: finalStatus,
          durationMs: event.durationMs ?? null,
          lastJobAt: Date.now(),
          hasErrors,
        }));
        es.close();
        esRef.current = null;
        activeStreamJobIdRef.current = null;
        activeStreamStoreIdRef.current = null;
      }

      if (event.type === "not_found") {
        es.close();
        esRef.current = null;
        activeStreamJobIdRef.current = null;
        activeStreamStoreIdRef.current = null;
        setState(prev => ({
          ...prev,
          status: prev.status === "running" ? "error" : prev.status,
          steps: prev.steps.length === 0
            ? [{ stepId: "init", label: "Задача не найдена", status: "error", error: "Обновление не найдено — попробуйте снова" }]
            : prev.steps,
        }));
      }
    };

    es.onerror = () => {
      es.close();
      esRef.current = null;
      activeStreamJobIdRef.current = null;
      activeStreamStoreIdRef.current = null;
      setState(prev => ({
        ...prev,
        status: prev.status === "running" ? "error" : prev.status,
      }));
    };
  }, [currentStoreId]);

  const startRefresh = useCallback(async (
    stepIds?: string[],
    storeId?: string | null,
    options?: { includeDailyAnalytics?: boolean; openModal?: boolean },
  ) => {
    const explicitStoreId = typeof storeId === "string" && storeId.length > 0 ? storeId : null;
    const targetStoreId = explicitStoreId;
    if (storeMode === "all" && !targetStoreId) {
      setState(prev => ({
        ...prev,
        status: "error",
        steps: [{
          stepId: "init",
          label: "Обновление недоступно",
          status: "error",
          error: "В режиме «Все магазины» обновление недоступно. Выберите конкретный магазин.",
        }],
        durationMs: null,
        targetStoreId: null,
        hasErrors: true,
        modalOpen: true,
      }));
      return;
    }
    const refreshStoreId = targetStoreId ?? currentStoreId;

    // Обновление не должно перехватывать экран: прогресс остаётся в компактном
    // индикаторе шапки. Полный журнал открываем только явным действием пользователя
    // (либо сохраняем его, если пользователь уже смотрит журнал).
    setState(prev => ({
      ...prev,
      status: "running",
      steps: [],
      durationMs: null,
      targetStoreId: refreshStoreId ?? null,
      hasErrors: false,
      modalOpen: options?.openModal === true ? true : prev.modalOpen,
    }));

    try {
      const body: { steps?: string[]; storeId?: string; includeDailyAnalytics?: true } = {};
      if (stepIds && stepIds.length > 0) body.steps = stepIds;
      if (targetStoreId) body.storeId = targetStoreId;
      if (options?.includeDailyAnalytics) body.includeDailyAnalytics = true;
      const hasBody = Object.keys(body).length > 0;
      const fetchOpts: RequestInit = hasBody
        ? {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
        : { method: "POST" };
      const res = await fetch("/api/refresh", fetchOpts);
      const data = await res.json();

      if (res.status === 409) {
        // Already running — connect to existing job
        if (data.jobId) connectToStream(data.jobId, options?.openModal === true, refreshStoreId);
        return;
      }

      if (!res.ok) {
        // Специальные коды от бэкенда:
        // - OZON_KEYS_INVALID / OZON_KEYS_MISSING — проблемы с ключами, показываем
        //   баннер с инструкцией перейти в Настройки.
        // - SERVER_BUSY — все слоты пайплайна заняты, юзеру предлагается
        //   повторить через минуту.
        const isAuth = data.code === "OZON_KEYS_INVALID" || data.code === "OZON_KEYS_MISSING";
        const isBusy = data.code === "SERVER_BUSY";
        setState(prev => ({
          ...prev,
          status: isAuth ? "auth_error" : "error",
          // Ошибка подключения требует действия: показать причину сразу,
          // даже если пользователь запускал обновление из компактной шапки.
          modalOpen: isAuth || prev.modalOpen,
          steps: isAuth || isBusy
            ? (isBusy
                ? [{ stepId: "init", label: "Сервер занят", status: "error", error: data.error ?? "Сервер сейчас занят, попробуйте через минуту" }]
                : [{ stepId: "init", label: "Подключение Ozon", status: "error", error: data.error ?? "Проверьте ключи Ozon в настройках магазина" }])
            : [{ stepId: "init", label: "Запуск", status: "error", error: data.error ?? "Ошибка запуска" }],
        }));
        return;
      }

      if (data.jobId) {
        connectToStream(data.jobId, options?.openModal === true, refreshStoreId);
      } else {
        // Сервер вернул 200 но без jobId — значит пайплайн умер ДО INSERT в
        // refresh_jobs (например, шаг инициализации кинул до jobId=lastInsertRowid).
        // Без этой ветки UI застревал в "running" навсегда, потому что SSE
        // не к чему подключаться.
        setState(prev => ({
          ...prev,
          status: "error",
          steps: [{ stepId: "init", label: "Запуск", status: "error", error: "Обновление не запустилось — попробуйте ещё раз" }],
        }));
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Сетевая ошибка";
      setState(prev => ({
        ...prev,
        status: "error",
        steps: [{ stepId: "init", label: "Запуск", status: "error", error: message }],
      }));
    }
  }, [connectToStream, currentStoreId, storeMode]);

  const attachToJob = useCallback((jobId: number, openModal = false) => {
    connectToStream(jobId, openModal, currentStoreId);
  }, [connectToStream, currentStoreId]);

  const retryStep: RefreshCtx["retryStep"] = useCallback(() => {
    // For now, trigger full refresh. Individual step retry can be added later.
    startRefresh(undefined, state.targetStoreId);
  }, [startRefresh, state.targetStoreId]);

  const openModal  = useCallback(() => setState(prev => ({ ...prev, modalOpen: true  })), []);
  const closeModal = useCallback(() => setState(prev => ({ ...prev, modalOpen: false })), []);

  const copyLog = useCallback((): string => {
    const { steps, durationMs, startedAt, status } = state;
    const date = startedAt ? new Date(startedAt).toLocaleString("ru-RU") : "—";
    const dur = durationMs ? `${Math.round(durationMs / 1000)}с` : "";

    const icon = (s: StepProgress["status"]) =>
      s === "done" ? "✓" : s === "warn" ? "⚠" : s === "error" ? "✗" : s === "loading" ? "⟳" : "○";

    const lines = [
      `Обновление ${date}${dur ? " · " + dur : ""}`,
      "─".repeat(40),
      ...steps.map(s => {
        const cnt = s.count != null ? ` (${s.count})` : "";
        const err = s.error ? `\n   → ${s.error}` : "";
        return `${icon(s.status)} ${s.label}${cnt}${err}`;
      }),
      "",
      `Итог: ${steps.filter(s => s.status === "done").length} ✓  `
        + `${steps.filter(s => s.status === "warn").length} ⚠  `
        + `${steps.filter(s => s.status === "error").length} ✗`,
      `Статус: ${status}`,
    ];
    return lines.join("\n");
  }, [state]);

  // Мемоизируем value: все методы — стабильные useCallback, поэтому объект
  // меняется только при смене state. Без этого каждый ре-рендер провайдера
  // (в т.ч. при навигации, т.к. он в корневом layout) создавал новый объект и
  // дёргал ре-рендер всех потребителей контекста.
  const value = useMemo(
    () => ({ ...state, startRefresh, attachToJob, retryStep, openModal, closeModal, copyLog }),
    [state, startRefresh, attachToJob, retryStep, openModal, closeModal, copyLog],
  );

  return (
    <RefreshContext.Provider value={value}>
      {children}
    </RefreshContext.Provider>
  );
}
